import { createContext, useContext, useState } from "react";
import { authClient } from "../auth/auth-client.js";
import {
  adminViewScope,
  readAdminViewChoice,
  writeAdminViewChoice,
  type AdminViewMode,
} from "../app/admin-view-choice.js";

interface AdminViewContext {
  canChoose: boolean;
  mode: AdminViewMode;
  showAdminTools: boolean;
  needsChoice: boolean;
  choosing: boolean;
  openChoice(): void;
  select(mode: AdminViewMode): void;
}
const Context = createContext<AdminViewContext>({
  canChoose: false,
  mode: "player",
  showAdminTools: false,
  needsChoice: false,
  choosing: false,
  openChoice() {},
  select() {},
});
export const useAdminView = () => useContext(Context);

export function AdminViewProvider(props: {
  accountId: string;
  bootstrapAccountId: string | null;
  role: "user" | "admin";
  authenticated: boolean;
  children: React.ReactNode;
}): React.JSX.Element {
  const session = authClient.useSession();
  const identity = {
    accountId: props.accountId,
    bootstrapAccountId: props.bootstrapAccountId,
    role: props.role,
    authenticated: props.authenticated,
    sessionAccountId: session.data?.user.id ?? null,
    sessionId: session.data?.session.id ?? null,
  };
  const scope = adminViewScope(identity);
  const [selection, setSelection] = useState<{
    scope: string;
    mode: AdminViewMode;
  } | null>(null);
  const [openScope, setOpenScope] = useState<string | null>(null);
  const choice =
    scope && selection?.scope === scope
      ? selection.mode
      : readAdminViewChoice(choiceStorage(), identity);
  const mode = scope && choice ? choice : "player";
  const value: AdminViewContext = {
    canChoose: scope !== null,
    mode,
    showAdminTools: scope !== null && mode === "admin",
    needsChoice: scope !== null && choice === null,
    choosing: scope !== null && openScope === scope,
    openChoice: () => setOpenScope(scope),
    select: (next) => {
      if (!scope) return;
      writeAdminViewChoice(choiceStorage(), identity, next);
      setSelection({ scope, mode: next });
      setOpenScope(null);
    },
  };
  return <Context.Provider value={value}>{props.children}</Context.Provider>;
}

function choiceStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}
